// Empty / error / loading states and inline alerts (plan 8.4 common states; mockup ui.empty / ui.error / ui.skeleton /
// ui.emptyConnect / ui.alert, elements 088 · 090 · 079 · 072).
import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useT } from '../i18n';
import { reduceMotion } from '../lib/media';
import { cls, cssEscape } from '../lib/util';
import { Icon, type IconName } from './Icon';

export interface StateAction {
  label: ReactNode;
  to?: string;
  onClick?: () => void;
}
function ActionButton({ a, className }: { a: StateAction; className: string }) {
  return a.to ? (
    <Link className={className} to={a.to}>
      {a.label}
    </Link>
  ) : (
    <button className={className} type="button" onClick={a.onClick}>
      {a.label}
    </button>
  );
}

function EmptyArt() {
  return (
    <svg className="state__art" viewBox="0 0 120 92" aria-hidden="true" focusable="false">
      <rect x="18" y="26" width="84" height="56" rx="12" className="art-a" />
      <path d="M18 40a8 8 0 0 1 8-8h22l8 8h38a8 8 0 0 1 8 8" className="art-b" />
      <rect x="32" y="12" width="44" height="34" rx="6" className="art-c" />
      <path d="M40 22h28M40 30h20" className="art-d" />
    </svg>
  );
}

/** Empty state: what is missing · (optional) what it does not mean · one next action. */
export function Empty({ title, desc, action, icon, compact }: { title?: ReactNode; desc?: ReactNode; action?: StateAction; icon?: IconName; compact?: boolean }) {
  const t = useT();
  return (
    <div className={cls('state', compact && 'state--compact')} role="status">
      {icon ? (
        <span className="state__ic">
          <Icon name={icon} />
        </span>
      ) : (
        <EmptyArt />
      )}
      <p className="state__title">{title ?? t('state.emptyTitle')}</p>
      {desc ? <p className="state__body">{desc}</p> : null}
      {action ? <ActionButton a={action} className="btn btn--primary" /> : null}
    </div>
  );
}

/** "Before connecting" empty state (088): still folder + 3 lines + [도구 연결하기]. */
export function EmptyConnect({ title, lines, action, className }: { title?: ReactNode; lines?: ReactNode[]; action?: { label: ReactNode; to: string }; className?: string }) {
  const t = useT();
  const act = action ?? { label: t('empty.connect.btn'), to: '/connect?tab=new' };
  return (
    <div className={cls('state', 'state--connect', className)} role="status">
      <svg className="state__folder" viewBox="0 0 168 128" aria-hidden="true" focusable="false">
        <path className="fd-back" d="M16 26a10 10 0 0 1 10-10h34a8 8 0 0 1 6 2.6l8 8.4h68a10 10 0 0 1 10 10v66a10 10 0 0 1-10 10H26a10 10 0 0 1-10-10z" />
        <rect className="fd-paper" x="40" y="28" width="88" height="62" rx="6" />
        <path className="fd-line" d="M54 42h44M54 52h56" />
        <path className="fd-front" d="M10 50a10 10 0 0 1 10-10h128a10 10 0 0 1 10 10.8l-5 52a10 10 0 0 1-10 9.2H25a10 10 0 0 1-10-9.2z" />
        <path className="fd-hi" d="M22 47h124" />
        <rect className="fd-slot" x="66" y="72" width="36" height="8" rx="4" />
      </svg>
      <p className="state__title">{title ?? t('empty.connect.title')}</p>
      {(lines ?? [t('empty.connect.l1'), t('empty.connect.l2')]).map((line, i) => (
        <p key={i} className="state__line">
          {line}
        </p>
      ))}
      <Link className="btn btn--primary state__act" to={act.to}>
        <Icon name="plug" />
        {act.label}
      </Link>
    </div>
  );
}

/**
 * Load failure (090, no flames or shaking): "불러오지 못함" pill · title · description · [자세히] reveals the error
 * number (request id) · [지금 다시 시도]. role=alert so screen readers hear it at once.
 */
export function ErrorState({ title, desc, code, onRetry }: { title?: ReactNode; desc?: ReactNode; code?: string | null; onRetry?: () => void }) {
  const t = useT();
  const [more, setMore] = useState(false);
  const did = useId();
  return (
    <div className="state state--error" role="alert">
      <span className="status-pill status-pill--danger">{t('state.failPill')}</span>
      <p className="state__title">{title ?? t('state.failTitle')}</p>
      {desc ? <p className="state__body">{desc}</p> : null}
      {code ? (
        <>
          <button className="state__more" type="button" aria-expanded={more} aria-controls={did} onClick={() => setMore((v) => !v)}>
            {t('state.more')}
            <Icon name="chevD" />
          </button>
          <div className="state__detail" id={did} hidden={!more}>
            <p className="state__code">
              {t('state.errCode')} <code>{code}</code>
            </p>
          </div>
        </>
      ) : null}
      {onRetry ? (
        <button className="btn state__retry" type="button" onClick={onRetry}>
          <Icon name="refresh" />
          {t('common.retryNow')}
        </button>
      ) : null}
    </div>
  );
}

/** Loading skeleton (079): shimmer 1.4 s, still grey with reduced motion. */
export function Skeleton({ lines = 3 }: { lines?: number }) {
  const t = useT();
  return (
    <div className="skeleton-block" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton skeleton--line" style={{ width: `${i === lines - 1 ? 60 : 100 - (i % 3) * 12}%` }} />
      ))}
    </div>
  );
}

/** After [지금 다시 시도] the card/screen is drawn again: move focus to that card's title (or the screen h1). */
export function focusAfterRetry(cardId?: string): void {
  requestAnimationFrame(() => {
    const el =
      (cardId && document.querySelector<HTMLElement>(`[data-card="${cssEscape(cardId)}"] .card__q`)) || document.querySelector<HTMLElement>('#main h1');
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  });
}

const ALERT_IC: Record<string, IconName> = { warn: 'warn', danger: 'alert', info: 'info', ok: 'ok' };
const dismissed = new Set<string>();

export interface AlertProps {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  title: ReactNode;
  desc?: ReactNode;
  /** Once dismissed, an alert with the same id stays hidden for this page visit. */
  id?: string;
  dismissible?: boolean;
  action?: StateAction;
  /** Accessible name of the ✕ (default: the title text). */
  dismissLabel?: string;
}
/** Inline alert (072): danger is role=alert, the rest role=status. */
export function Alert({ tone = 'info', title, desc, id, dismissible, action, dismissLabel }: AlertProps) {
  const t = useT();
  const [gone, setGone] = useState(() => !!id && dismissed.has(id));
  const [leaving, setLeaving] = useState(false);
  if (gone) return null;
  const dismiss = () => {
    if (id) dismissed.add(id);
    setLeaving(true);
    setTimeout(() => setGone(true), reduceMotion() ? 0 : 320);
  };
  return (
    <div className="inline-alert-wrap">
      <div className={cls('inline-alert', `inline-alert--${tone}`, leaving && 'is-leaving')} role={tone === 'danger' ? 'alert' : 'status'}>
        <span className="inline-alert__ic" aria-hidden="true">
          <Icon name={ALERT_IC[tone] ?? 'info'} />
        </span>
        <div className="inline-alert__body">
          <b>{title}</b>
          {desc ? <p>{desc}</p> : null}
          {action ? (
            action.to ? (
              <Link className="inline-alert__act" to={action.to}>
                {action.label} →
              </Link>
            ) : (
              <button className="inline-alert__act" type="button" onClick={action.onClick}>
                {action.label}
              </button>
            )
          ) : null}
        </div>
        {dismissible ? (
          <button
            className="inline-alert__x"
            type="button"
            aria-label={`${t('common.dismiss')}: ${dismissLabel ?? (typeof title === 'string' ? title : '')}`}
            data-tip={t('common.dismiss')}
            onClick={dismiss}
          >
            <Icon name="x" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
